import { Injectable, signal, computed, inject, NgZone } from '@angular/core';
import { AuthService } from './auth.service';

/** How long “Not now” hides the auto install prompt. */
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/** After this many dismissals, stop auto-prompting (user can still install via browser UI). */
const MAX_AUTO_DISMISSALS = 2;

/** Wait so shell, menu, and session restore finish before prompting. */
const SHOW_DELAY_MS = 12_000;

const LS_DISMISSED = 'pwa-dismissed';
const LS_DISMISSED_TIME = 'pwa-dismissed-time';
const LS_DISMISS_COUNT = 'pwa-dismiss-count';
const LS_INSTALLED = 'pwa-installed';

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  private promptEvent: any;
  private readonly authService = inject(AuthService);
  private readonly zone = inject(NgZone);
  private showTimer: ReturnType<typeof setTimeout> | null = null;

  /** True when the native browser install prompt is available (Chrome/Edge/Android) */
  private readonly _canInstallNative = signal(false);
  readonly canInstallNative = this._canInstallNative.asReadonly();

  /** Mirrors AuthService so computed banner visibility stays reactive */
  private readonly _isLoggedIn = signal(false);

  /** True when the user snoozed / permanently declined auto-prompt */
  private readonly _dismissed = signal(false);
  readonly dismissed = this._dismissed.asReadonly();

  /** Delay showing so it does not fight first paint / menu open */
  private readonly _readyToShow = signal(false);

  /** True when the app is already running as an installed PWA */
  readonly isStandalone = signal(
    typeof window !== 'undefined' &&
      (window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true),
  );

  /** Detected platform for showing correct install instructions */
  readonly platform = signal<'ios' | 'android' | 'desktop'>(this.detectPlatform());

  /** Cache-busted icon URL — updates when icon files change between releases */
  readonly iconSrc = signal('icons/icon-192x192.png');

  /**
   * Reactive banner visibility. Uses a non-blocking bottom card (see component)
   * so the nav/menu stay usable while the prompt is visible.
   */
  readonly shouldShowBanner = computed(() => {
    if (this.isStandalone()) return false;
    if (!this._isLoggedIn()) return false;
    if (this.platform() === 'desktop') return false;
    if (!this._readyToShow()) return false;
    if (this._dismissed()) return false;

    if (typeof window !== 'undefined') {
      // Hard stop after repeated “Not now”
      const count = parseInt(localStorage.getItem(LS_DISMISS_COUNT) || '0', 10);
      if (Number.isFinite(count) && count >= MAX_AUTO_DISMISSALS) {
        return false;
      }

      // Cooldown after dismiss (survives reloads)
      const dismissedTime = localStorage.getItem(LS_DISMISSED_TIME);
      if (dismissedTime) {
        const diff = Date.now() - parseInt(dismissedTime, 10);
        if (Number.isFinite(diff) && diff < DISMISS_COOLDOWN_MS) {
          return false;
        }
      }

      // User completed install — do not nag in browser tabs either
      if (localStorage.getItem(LS_INSTALLED) === 'true' && !this.canInstallNative()) {
        return false;
      }
    }

    // iOS: manual instructions; Android/Chrome: only when browser allows install
    return this.platform() === 'ios' || this.canInstallNative();
  });

  constructor() {
    if (typeof window === 'undefined') return;

    this._isLoggedIn.set(this.authService.isLoggedIn);
    this.authService.currentUser$.subscribe(() => {
      this._isLoggedIn.set(this.authService.isLoggedIn);
    });

    this.hydrateDismissState();

    fetch('pwa-build.json', { cache: 'no-store', headers: { 'ngsw-bypass': 'true' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { iconQuery?: string } | null) => {
        if (data?.iconQuery) {
          this.iconSrc.set(`icons/icon-192x192.png${data.iconQuery}`);
        }
      })
      .catch(() => undefined);

    // Outside Angular first paint path; re-enter zone when ready
    this.zone.runOutsideAngular(() => {
      this.showTimer = setTimeout(() => {
        this.zone.run(() => this._readyToShow.set(true));
      }, SHOW_DELAY_MS);
    });
  }

  public initPwaPrompt(): void {
    if (typeof window === 'undefined') return;

    // 1. Event may already be stashed by index.html
    if ((window as any).deferredPrompt) {
      this.promptEvent = (window as any).deferredPrompt;
      this._canInstallNative.set(true);
    }

    // 2. Callback if early script captures the event while Angular boots
    (window as any).onDeferredPrompt = (event: any) => {
      this.promptEvent = event;
      this._canInstallNative.set(true);
    };

    // 3. Custom install UI (prevent mini-infobar)
    window.addEventListener('beforeinstallprompt', (event: any) => {
      event.preventDefault();
      this.promptEvent = event;
      this._canInstallNative.set(true);
    });

    window.addEventListener('appinstalled', () => {
      this._canInstallNative.set(false);
      this.promptEvent = null;
      this.isStandalone.set(true);
      this._dismissed.set(true);
      localStorage.setItem(LS_INSTALLED, 'true');
      this.clearDismissCooldown();
    });
  }

  /** Trigger the native browser install prompt */
  public installPwa(): void {
    if (!this.promptEvent) return;

    this.promptEvent.prompt();
    this.promptEvent.userChoice.then((choiceResult: { outcome: string }) => {
      if (choiceResult.outcome === 'accepted') {
        localStorage.setItem(LS_INSTALLED, 'true');
        this._dismissed.set(true);
        this.clearDismissCooldown();
      } else {
        // Treated like “Not now” so we do not immediately re-open
        this.dismissBanner();
      }
      this.promptEvent = null;
      this._canInstallNative.set(false);
    });
  }

  /** Snooze the install banner (long cooldown; permanent after repeated dismissals) */
  public dismissBanner(): void {
    this._dismissed.set(true);
    if (typeof window === 'undefined') return;

    const prev = parseInt(localStorage.getItem(LS_DISMISS_COUNT) || '0', 10);
    const next = (Number.isFinite(prev) ? prev : 0) + 1;
    localStorage.setItem(LS_DISMISS_COUNT, String(next));
    localStorage.setItem(LS_DISMISSED, 'true');
    localStorage.setItem(LS_DISMISSED_TIME, Date.now().toString());
  }

  private hydrateDismissState(): void {
    const count = parseInt(localStorage.getItem(LS_DISMISS_COUNT) || '0', 10);
    if (Number.isFinite(count) && count >= MAX_AUTO_DISMISSALS) {
      this._dismissed.set(true);
      return;
    }

    const dismissedTime = localStorage.getItem(LS_DISMISSED_TIME);
    if (dismissedTime) {
      const diff = Date.now() - parseInt(dismissedTime, 10);
      if (Number.isFinite(diff) && diff < DISMISS_COOLDOWN_MS) {
        this._dismissed.set(true);
        return;
      }
      // Cooldown expired — allow a rare re-prompt, but keep dismiss count
      localStorage.removeItem(LS_DISMISSED);
      localStorage.removeItem(LS_DISMISSED_TIME);
      this._dismissed.set(false);
      return;
    }

    // Legacy flag without timestamp: respect as dismissed once, then stamp cooldown
    if (localStorage.getItem(LS_DISMISSED) === 'true') {
      localStorage.setItem(LS_DISMISSED_TIME, Date.now().toString());
      this._dismissed.set(true);
    }
  }

  private clearDismissCooldown(): void {
    localStorage.removeItem(LS_DISMISSED);
    localStorage.removeItem(LS_DISMISSED_TIME);
  }

  private detectPlatform(): 'ios' | 'android' | 'desktop' {
    if (typeof navigator === 'undefined') return 'desktop';
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    return 'desktop';
  }
}
